import { redirect } from '@tanstack/react-router';

import type { Organization, OrganizationRole } from '@/features/organizations/api/schemas';
import { listOrganizations } from '@/features/organizations/services/api';
import { getOrganizationBilling } from '@/features/organizations/services/billing';
import { isLifetimeBilling } from '@/features/organizations/services/billing-source';
import { billingKeys, organizationKeys } from '@/features/organizations/services/queryKeys';
import { useCurrentOrganizationStore } from '@/features/organizations/store/useCurrentOrganizationStore';
import { getUserDisplayName, keycloak } from '@/lib/auth';
import { ensureKeycloakInit } from '@/lib/auth-bootstrap';
import { appQueryClient } from '@/lib/query-client';
import { getAppBootstrapSnapshot } from '@/providers/app-bootstrap-snapshot';

async function resolveAuthenticatedState(): Promise<boolean> {
    const bootstrapSnapshot = getAppBootstrapSnapshot();

    if (bootstrapSnapshot?.status === 'ready') {
        return bootstrapSnapshot.isAuthenticated;
    }

    return ensureKeycloakInit();
}

async function listOrganizationsForGuard(): Promise<Organization[] | null> {
    try {
        const organizations = await appQueryClient.ensureQueryData({
            queryKey: organizationKeys.list(),
            queryFn: listOrganizations,
            retry: false
        });

        return Array.isArray(organizations) ? organizations : null;
    } catch {
        return null;
    }
}

async function getAuthenticatedOrganizationsCount(): Promise<number | null> {
    const bootstrapSnapshot = getAppBootstrapSnapshot();
    const isAuthenticated =
        bootstrapSnapshot?.status === 'ready' ? bootstrapSnapshot.isAuthenticated : await ensureKeycloakInit();

    if (!isAuthenticated) {
        return null;
    }

    const organizations = await listOrganizationsForGuard();

    if (organizations) {
        return organizations.length;
    }

    return bootstrapSnapshot?.status === 'ready' ? bootstrapSnapshot.organizationsCount : 0;
}

export async function requireAuthenticatedRoute() {
    const isAuthenticated = await resolveAuthenticatedState();

    if (!isAuthenticated) {
        throw redirect({
            to: '/'
        });
    }

    return {
        username: getUserDisplayName(),
        isAuthenticated: Boolean(keycloak.authenticated || keycloak.token)
    };
}

export async function redirectAuthenticatedUsersWithoutOrganizations() {
    const organizationsCount = await getAuthenticatedOrganizationsCount();

    if (organizationsCount === 0) {
        throw redirect({
            to: '/organizations'
        });
    }
}

async function getActiveOrganization(): Promise<Organization | null> {
    const organizations = await listOrganizationsForGuard();

    if (!organizations || organizations.length === 0) {
        return null;
    }

    const currentOrganizationId = useCurrentOrganizationStore.getState().currentOrganizationId;
    const currentOrganization = currentOrganizationId
        ? organizations.find((organization) => organization.id === currentOrganizationId)
        : null;

    return currentOrganization ?? organizations[0];
}

async function getActiveOrganizationRole(): Promise<OrganizationRole | null> {
    const organization = await getActiveOrganization();

    return organization ? organization.membership.role : null;
}

export async function requireOrganizationRoute() {
    await requireAuthenticatedRoute();

    const organizationsCount = await getAuthenticatedOrganizationsCount();

    if (organizationsCount === 0) {
        throw redirect({
            to: '/organizations'
        });
    }
}

export const redirectLifetimeBillingFromCheckoutRoute = async () => {
    if (!(await resolveAuthenticatedState())) {
        return;
    }

    const organization = await getActiveOrganization();

    if (!organization) {
        return;
    }

    try {
        const billing = await appQueryClient.ensureQueryData({
            queryKey: billingKeys.organization(organization.id),
            queryFn: () => getOrganizationBilling(organization.id),
            retry: false
        });

        if (!isLifetimeBilling(billing)) {
            return;
        }
    } catch {
        return;
    }

    throw redirect({
        to: '/settings'
    });
};

export async function requireOrganizationRoleRoute(canAccess: (role: OrganizationRole) => boolean) {
    await requireOrganizationRoute();

    const role = await getActiveOrganizationRole();

    if (!role || !canAccess(role)) {
        throw redirect({
            to: '/organizations'
        });
    }
}
